import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiUpload, FiFileText, FiSave, FiX, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import Layout from '../components/Layout/Layout';
import api from '../services/api';

const Container = styled.div`
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  margin: 0;
`;

const Form = styled.form`
  background: ${props => props.theme?.colors?.surface || '#ffffff'};
  border-radius: 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  padding: 24px;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.cols || '1fr'};
  gap: 16px;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 8px;
  background: ${props => props.theme?.colors?.background || '#ffffff'};
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.primary || '#6366f1'};
  }
`;

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 8px;
  background: ${props => props.theme?.colors?.background || '#ffffff'};
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.primary || '#6366f1'};
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 8px;
  background: ${props => props.theme?.colors?.background || '#ffffff'};
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.primary || '#6366f1'};
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
  
  label {
    font-size: 14px;
    color: ${props => props.theme?.colors?.text || '#1a1a2e'};
    cursor: pointer;
  }
`;

const FileUpload = styled.div`
  border: 2px dashed ${props => props.hasFile ? props.theme?.colors?.primary || '#6366f1' : props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.hasFile ? (props.theme?.colors?.primary || '#6366f1') + '10' : 'transparent'};
  
  &:hover {
    border-color: ${props => props.theme?.colors?.primary || '#6366f1'};
  }
  
  input {
    display: none;
  }
`;

const FileIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto 16px;
  border-radius: 12px;
  background: ${props => props.theme?.colors?.backgroundAlt || '#f3f4f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: ${props => props.theme?.colors?.primary || '#6366f1'};
`;

const FileInfo = styled.div`
  font-size: 14px;
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  
  span {
    color: ${props => props.theme?.colors?.textSecondary || '#888'};
    font-size: 13px;
    display: block;
    margin-top: 4px;
  }
`;

const VariablesPreview = styled.div`
  background: ${props => props.theme?.colors?.backgroundAlt || '#f3f4f6'};
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
`;

const VariablesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const VariableTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${props => props.theme?.colors?.surface || '#ffffff'};
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 6px;
  font-size: 13px;
  font-family: monospace;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &.primary {
    background: ${props => props.theme?.colors?.primary || '#6366f1'};
    color: white;
    
    &:hover {
      background: ${props => props.theme?.colors?.primaryDark || '#4f46e5'};
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background: ${props => props.theme?.colors?.backgroundAlt || '#f3f4f6'};
    color: ${props => props.theme?.colors?.text || '#1a1a2e'};
    
    &:hover {
      background: ${props => props.theme?.colors?.border || '#e5e7eb'};
    }
  }
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  
  &.error {
    background: #fee2e2;
    color: #dc2626;
  }
  
  &.success {
    background: #dcfce7;
    color: #16a34a;
  }
`;

const CATEGORIES = [
  { value: 'sales', label: 'Sales Agreement' },
  { value: 'service', label: 'Service Contract' },
  { value: 'nda', label: 'Non-Disclosure Agreement' },
  { value: 'partnership', label: 'Partnership Agreement' },
  { value: 'employment', label: 'Employment Contract' },
  { value: 'consulting', label: 'Consulting Agreement' },
  { value: 'subscription', label: 'Subscription Agreement' },
  { value: 'other', label: 'Other' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'nl', label: 'Dutch' },
];

export default function ContractTemplateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'sales',
    language: 'en',
    is_active: true,
    is_default: false,
  });
  
  const [file, setFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const [variables, setVariables] = useState([]);

  const fetchTemplate = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/contracts/templates/${id}`);
      const template = response.data.template;
      
      setFormData({
        name: template.name,
        description: template.description || '',
        category: template.category || 'sales',
        language: template.language || 'en',
        is_active: template.is_active,
        is_default: template.is_default,
      });
      
      setExistingFile({
        name: template.file_name,
        size: template.file_size,
      });
      
      setVariables(template.variables || []);
    } catch (err) {
      setError('Failed to load template');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      fetchTemplate();
    }
  }, [isEdit, fetchTemplate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    // Validate file type
    if (!selectedFile.name.endsWith('.docx')) {
      setError('Please upload a .docx file');
      return;
    }
    
    setFile(selectedFile);
    setError(null);
    
    // Preview variables from file
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await api.post('/contracts/templates/preview-variables', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setVariables(response.data.variables || []);
    } catch (err) {
      console.error('Error previewing variables:', err);
      // Don't show error - file upload still valid
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isEdit && !file) {
      setError('Please upload a template file');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      const data = new FormData();
      data.append('name', formData.name);
      data.append('description', formData.description);
      data.append('category', formData.category);
      data.append('language', formData.language);
      data.append('is_active', formData.is_active);
      data.append('is_default', formData.is_default);
      
      if (file) {
        data.append('file', file);
      }
      
      if (isEdit) {
        await api.put(`/contracts/templates/${id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/contracts/templates', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      setSuccess('Template saved successfully!');
      setTimeout(() => navigate('/contracts/templates'), 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save template');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb.toFixed(1)} KB`;
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <p>Loading template...</p>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Header>
          <Title>{isEdit ? 'Edit Template' : 'New Contract Template'}</Title>
        </Header>

        {error && (
          <Alert className="error">
            <FiAlertCircle /> {error}
          </Alert>
        )}

        {success && (
          <Alert className="success">
            <FiCheckCircle /> {success}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>Template Details</SectionTitle>
            
            <FormRow>
              <FormGroup>
                <Label>Template Name *</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Standard Sales Agreement"
                  required
                />
              </FormGroup>
            </FormRow>
            
            <FormRow>
              <FormGroup>
                <Label>Description</Label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of what this template is used for..."
                />
              </FormGroup>
            </FormRow>
            
            <FormRow cols="1fr 1fr">
              <FormGroup>
                <Label>Category</Label>
                <Select name="category" value={formData.category} onChange={handleInputChange}>
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Language</Label>
                <Select name="language" value={formData.language} onChange={handleInputChange}>
                  {LANGUAGES.map(lang => (
                    <option key={lang.value} value={lang.value}>{lang.label}</option>
                  ))}
                </Select>
              </FormGroup>
            </FormRow>
            
            <FormRow cols="1fr 1fr">
              <Checkbox>
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <label htmlFor="is_active">Active (available for use)</label>
              </Checkbox>
              
              <Checkbox>
                <input
                  type="checkbox"
                  id="is_default"
                  name="is_default"
                  checked={formData.is_default}
                  onChange={handleInputChange}
                />
                <label htmlFor="is_default">Default template for this category</label>
              </Checkbox>
            </FormRow>
          </FormSection>

          <FormSection>
            <SectionTitle>Template File</SectionTitle>
            
            <FileUpload 
              hasFile={!!file || !!existingFile} 
              onClick={() => document.getElementById('fileInput').click()}
            >
              <input
                type="file"
                id="fileInput"
                accept=".docx"
                onChange={handleFileChange}
              />
              <FileIcon>
                <FiFileText />
              </FileIcon>
              {file ? (
                <FileInfo>
                  <strong>{file.name}</strong>
                  <span>{formatFileSize(file.size)}</span>
                </FileInfo>
              ) : existingFile ? (
                <FileInfo>
                  <strong>{existingFile.name}</strong>
                  <span>{formatFileSize(existingFile.size)} • Click to replace</span>
                </FileInfo>
              ) : (
                <FileInfo>
                  <strong>Click to upload template</strong>
                  <span>Supported format: .docx (Word document)</span>
                </FileInfo>
              )}
            </FileUpload>

            {variables.length > 0 && (
              <VariablesPreview>
                <Label style={{ marginBottom: 12, display: 'block' }}>
                  Detected Variables ({variables.length})
                </Label>
                <VariablesList>
                  {variables.map((v, i) => (
                    <VariableTag key={i}>
                      {'{{' + v.name + '}}'}
                    </VariableTag>
                  ))}
                </VariablesList>
              </VariablesPreview>
            )}
          </FormSection>

          <ButtonGroup>
            <Button type="button" className="secondary" onClick={() => navigate('/contracts/templates')}>
              <FiX /> Cancel
            </Button>
            <Button type="submit" className="primary" disabled={saving}>
              <FiSave /> {saving ? 'Saving...' : 'Save Template'}
            </Button>
          </ButtonGroup>
        </Form>
      </Container>
    </Layout>
  );
}
